import React from "react";
import restaurantImg from "../assets/restorent/restaurant.png";
import SEO from "../components/SEO";
import { aboutPageSchema } from "../data/schemas";
import FAQSection from "../components/FAQSection";
import { aboutFaqs } from "../data/faqs";

const AboutPage = () => {
  return (
    <div
      className="min-h-screen bg-[#f9f5f0]"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <SEO
        title="About Barbequeen | Top BBQ & Biryani Restaurant Since 1999"
        metaTitle="About Barbequeen | Top BBQ & Biryani Restaurant Since 1999"
        description="Learn about Barbequeen Restaurant, crafting premium Indian, Chinese, Tandoori & BBQ specialties since 1999. Discover our journey, values, and outdoor catering."
        keywords="Barbequeen story, about Barbequeen restaurant, Salem restaurant history, BBQ restaurant Tamil Nadu, outdoor catering Salem, family restaurant Salem"
        robots="index, follow"
        canonical="https://barbequeen.com/about-us"
        og={{
          type: "website",
          url: "https://barbequeen.com/about-us",
          title: "About Us | Our Story - Barbequeen Restaurant",
          description: "Learn about Barbequeen's 25+ year journey of crafting premium Indian, Chinese, Tandoori & BBQ specialties, our core values, and catering services.",
          image: "https://barbequeen.com/images/about-banner.jpg",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN"
        }}
        schema={aboutPageSchema}
      />
      {/* ── Hero Banner ── */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: "340px" }}
      >
        <img
          src="https://barbequeen.com/public/uploads/04-14-2023-1681461420.jpg"
          alt="About Barbequeen"
          className="absolute inset-0 w-full h-full object-cover object-center"
          onError={(e) => {
            e.target.src = restaurantImg;
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,0,2,0.72) 0%, rgba(10,0,2,0.82) 100%)",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p className="text-xs font-bold tracking-[0.5em] uppercase text-[#c0424e] mb-4">
            Our Story
          </p>
          <h1
            className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            About Us
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed">
            Crafting premium Indian, Chinese, Tandoori & Grill specialties since
            1999.
          </p>
        </div>
      </section>

      {/* ── About Barbequeen Restaurant Section ── */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Text Column */}
          <div className="lg:col-span-7 space-y-6">
            <span className="text-xs font-bold tracking-widest text-[#9b1c2e] uppercase block">
              Est. 1999
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              About Barbequeen Restaurant
            </h2>

            {/* Separator */}
            <div className="w-20 h-[3px] bg-[#9b1c2e]" />

            <p className="text-lg text-gray-800 font-semibold leading-relaxed">
              Mood to have Grill chicken or Biryani? Barbequeen Restaurant, The
              perfect restaurant that your heart longs for and satisfy your
              tastebuds.
            </p>

            <p className="text-sm text-gray-600 leading-relaxed">
              An expert in Grill, barbeque-style, tandoori, indian & chinese
              Cuisine since 1999, Barbequeen Restaurant has been the go-to place
              for family and friends for dine-in, takeaway, delivery, and
              online-delivery for foodies.
            </p>

            <p className="text-sm text-gray-600 leading-relaxed font-medium bg-[#9b1c2e]/5 border-l-4 border-[#9b1c2e] p-4 rounded-r-xl">
              From double marinated Grill chicken to chicken kabab, Butter Naan
              to paneer butter masala, shawarma to sea food you’ll find all
              kinds of hearty meals prepared fresh at The Barbequeen Restaurant.
            </p>

            <p className="text-sm text-gray-600 leading-relaxed">
              We’re here to serve you the best food around, whenever you’re
              looking for a great Indian restaurant.
            </p>
          </div>

          {/* Right Image Column */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-white bg-white">
              <img
                src={restaurantImg}
                alt="Barbequeen Restaurant interior"
                className="w-full h-auto object-cover rounded-2xl"
                onError={(e) => {
                  e.target.src =
                    "https://barbequeen.com/public/uploads/04-14-2023-1681461440.jpg";
                }}
              />
            </div>
            {/* Decorative background blob */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#9b1c2e]/10 rounded-full -z-10" />
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-[#9b1c2e]/10 rounded-full -z-10" />
          </div>
        </div>
      </section>

      {/* ── Statistics / Highlights Banner ── */}
      <section className="relative bg-black overflow-hidden py-16 text-white">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#9b1c2e] via-transparent to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p
                className="text-4xl sm:text-5xl font-black text-white"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                650+
              </p>
              <p className="text-xs uppercase font-bold tracking-widest text-white mt-2">
                Outdoor Catering
              </p>
            </div>

            <div>
              <p
                className="text-4xl sm:text-5xl font-black text-white"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                82+
              </p>
              <p className="text-xs uppercase font-bold tracking-widest text-white mt-2">
                Special Dishes
              </p>
            </div>

            <div>
              <p
                className="text-4xl sm:text-5xl font-black text-white"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                35+
              </p>
              <p className="text-xs uppercase font-bold tracking-widest text-white mt-2">
                Expert Chefs
              </p>
            </div>

            <div>
              <p
                className="text-4xl sm:text-5xl font-black text-white"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                15+
              </p>
              <p className="text-xs uppercase font-bold tracking-widest text-white mt-2">
                Barbequeen Outlets
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission & Vision Section ── */}
      <section className="w-full bg-black py-24 border-t border-b border-white/5 relative overflow-hidden">
        {/* Background Decorative elements */}
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 bg-[#9b1c2e]/5 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 bg-[#D4AF37]/5 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-6xl mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center max-w-xl mx-auto mb-16">
            <p className="text-xs font-bold tracking-[0.25em] text-[#D4AF37] uppercase mb-3">
              Our Core Principles
            </p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-wider leading-tight mb-2"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Our <span className="text-[#D4AF37]">Mission & Vision</span>
            </h2>
            
            {/* Elegant Divider from attached image */}
            <div className="flex items-center justify-center gap-4 py-2 mb-4">
              <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-[#D4AF37]/50" />
              <span className="text-[#D4AF37] text-xs select-none">✦</span>
              <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-[#D4AF37]/50" />
            </div>

            <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
              Exquisite Restaurant in City
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Our Vision",
                description: "Serves yummy food like mummy does.",
                icon: (
                  <svg className="w-7 h-7 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.644M7.5 12.339c.535 0 .97.435.97.97s-.435.97-.97.97c-.536 0-.97-.435-.97-.97s.435-.97.97-.97zM16.5 12.339c.535 0 .97.435.97.97s-.435.97-.97.97c-.536 0-.97-.435-.97-.97s.435-.97.97-.97z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )
              },
              {
                title: "Our Mission",
                description: "To be the go to place for family and friends for good food and great time.",
                icon: (
                  <svg className="w-7 h-7 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                )
              },
              {
                title: "Barbequeen Express",
                description: "Worried about not able to visit our restaurant? Not anymore.",
                icon: (
                  <svg className="w-7 h-7 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                )
              },
              {
                title: "Order Online",
                description: "Since the time we have started Barbequeen we have transformed the way people order food online.",
                icon: (
                  <svg className="w-7 h-7 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                )
              },
              {
                title: "Outdoor Catering",
                description: "Make your events special, memorable, and unforgettable experience.",
                icon: (
                  <svg className="w-7 h-7 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v3m0 0a6 6 0 016 6v1H6v-1a6 6 0 016-6zm-9 12h18a1 1 0 011 1v1H2v-1a1 1 0 011-1z" />
                  </svg>
                )
              },
              {
                title: "Events Promotions",
                description: "We take care of all the organisation and tasks to deliver a successful event, allowing you to focus on the day and your guests.",
                icon: (
                  <svg className="w-7 h-7 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499c.173-.434.762-.434.935 0l1.83 4.582a1 1 0 00.753.548l4.908.411c.473.04.662.624.298.918l-3.707 2.99a1 1 0 00-.317.977l1.016 4.792c.098.462-.405.828-.795.569l-4.228-2.775a1 1 0 00-1.026 0l-4.228 2.775c-.39.259-.893-.107-.795-.569l1.016-4.792a1 1 0 00-.317-.977L1.442 12.87c-.364-.294-.175-.878.298-.918l4.908-.411a1 1 0 00.753-.548l1.83-4.582z" />
                  </svg>
                )
              }
            ].map((principle, index) => (
              <div
                key={index}
                className="bg-[#121212]/90 backdrop-blur-sm p-8 rounded-2xl border border-[#D4AF37]/15 shadow-sm hover:shadow-md hover:border-[#D4AF37]/45 hover:-translate-y-1.5 transition-all duration-300 relative overflow-hidden group flex flex-col items-center text-center"
              >
                {/* Dotted border container for icon */}
                <div className="w-16 h-16 rounded-full border border-dashed border-[#D4AF37]/35 flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                  {principle.icon}
                </div>

                <h3
                  className="text-lg font-bold text-[#D4AF37] mb-3 tracking-wide uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {principle.title}
                </h3>

                <p className="text-sm text-gray-400 leading-relaxed font-sans">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection faqs={aboutFaqs} />
    </div>
  );
};

export default AboutPage;

